import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Baby, AlertTriangle } from "lucide-react";
import { useLang } from "../i18n";
import { useCheckin } from "../context/CheckinContext";
import { Button } from "../components/ui/button";

export default function Confirmation() {
  const { t } = useLang();
  const navigate = useNavigate();
  const { flow, reset } = useCheckin();

  useEffect(() => {
    if (!flow.result) navigate("/");
  }, [flow.result, navigate]);

  const kids = flow.result?.checkedIn || [];
  const steps = [t("step1"), t("step2"), t("step3"), t("step4")];

  const handleDone = () => {
    reset();
    navigate("/");
  };

  return (
    <div className="min-h-screen lf-grain bg-[#FFEEBC]" data-testid="confirmation-page">
      <main className="mx-auto max-w-lg px-5 py-12">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 200, damping: 15 }}
          className="mb-6 flex justify-center"
        >
          <div className="flex h-24 w-24 items-center justify-center rounded-full bg-[#26422A]">
            <CheckCircle2 className="h-14 w-14 text-[#FFEEBC]" strokeWidth={2} />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="text-center"
        >
          <h1 className="font-display text-3xl font-black text-[#26422A]">
            {t("requestSubmitted")}
          </h1>
          <p className="mt-2 font-medium text-[#26422A]">{t("checkinRequestReceived")}</p>
        </motion.div>

        {kids.length > 0 && (
          <div className="mt-8 rounded-3xl border-2 border-[#26422A]/40 bg-[#FFFFFF] p-6">
            <p className="mb-3 text-sm font-bold uppercase tracking-wide text-[#26422A]">
              {t("childrenCheckingIn")}
            </p>
            <div className="flex flex-wrap gap-2" data-testid="confirmation-children">
              {kids.map((c) => (
                <span
                  key={c.id}
                  className="inline-flex items-center gap-2 rounded-full bg-[#26422A] px-4 py-2 text-sm font-bold text-[#FFEEBC]"
                >
                  <Baby className="h-4 w-4" /> {c.name}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="mt-6 rounded-3xl border-2 border-[#26422A]/40 bg-[#FFFFFF] p-6">
          <h2 className="mb-4 font-display text-lg font-bold text-[#26422A]">
            {t("nextSteps")}
          </h2>
          <ol className="space-y-3">
            {steps.map((s, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#26422A] text-sm font-bold text-[#FFEEBC]">
                  {i + 1}
                </span>
                <span className="pt-0.5 font-medium text-[#26422A]">{s}</span>
              </li>
            ))}
          </ol>
        </div>

        <div className="mt-6 flex items-start gap-3 rounded-2xl border border-[#EA672D]/40 bg-[#FFFFFF] p-4">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-[#EA672D]" />
          <p className="text-sm font-medium text-[#26422A]">{t("importantNote")}</p>
        </div>

        <Button
          data-testid="confirmation-done-btn"
          onClick={handleDone}
          className="mt-8 h-14 w-full rounded-xl bg-[#26422A] text-base font-bold text-[#FFEEBC] hover:bg-[#1B3320]"
        >
          {t("done")}
        </Button>
      </main>
    </div>
  );
}
